import * as React from "react";
import { Form, Radio, Select, Spin } from "antd";
import { onError } from "src/common/onError";
import {
  FormBlockItem,
  ItemProps,
  blockItem,
} from "src/components/FormWithBlocks";
import { loadRestrictions } from "src/pages/ManagementPage/objectsApi";
import { ZAttribute } from "src/types/ZAttribute";
import { ZOption } from "src/types/ZOption";
import { ZRestriction } from "src/types/ZRestriction";
import { ZUid } from "src/types/zUid";
import { AttrSelectProps } from "src/common/attrEdit/components";
import { ifDef } from "src/common/ifDef";
import { loadRefOptions } from "src/common/getEntityName";
import { getAllowedSourceIds } from "../cascadeDependentAttrs";
import { fieldName } from "./fieldName";

/**
 * Компонент для выбора значения из справочника.
 * Поддерживает ограничения значений, в зависимости от другого поля.
 */

/**
 * Регистрация загруженного ограничения во внешнем сторе (LPLM-5173).
 * Вызывается после монтирования и загрузки loadRestrictions - до этого момента
 * стор о зависимости не знает (см. комментарий у EntityCardStore.restrictionsMap).
 */
export type OnRestrictionLoaded = (
  attrId: ZUid,
  restriction: ZRestriction,
) => void;

export const buildReferenceItem = (
  attr: ZAttribute,
  isMulti: boolean,
  itemProps: ItemProps,
  extraProps: AttrSelectProps,
  onRestrictionLoaded?: OnRestrictionLoaded,
): FormBlockItem =>
  blockItem(
    fieldName(attr),
    attr.name,
    ReferenceItem,
    {
      attr,
      isMulti,
      extraProps,
      onRestrictionLoaded,
    },
    itemProps,
  );

// Используется декомпозиция компонентов по той причине, что ограницения по значению являются опциональными
// Если отслеживать нестатическое поле, то будут ошибки.
// Поэтому поле отслеживается внутри отдельного компонента RefItemConditional, где оно уже безусловное.

interface PropsReferenceItem {
  value?: string[];
  onChange?(newValue?: unknown): void;
  attr: ZAttribute;
  isMulti: boolean;
  extraProps: AttrSelectProps;
  onRestrictionLoaded?: OnRestrictionLoaded;
}

export const ReferenceItem: React.FC<PropsReferenceItem> = (props) => {
  const {
    attr,
    value,
    onChange,
    isMulti,
    extraProps,
    extraProps: { restrictValues },
    onRestrictionLoaded,
  } = props;
  const { referenceId, viewType } = attr;
  const [wait, setWait] = React.useState(false);
  const [allOptions, setAllOptions] = React.useState<ZOption[]>([]);
  const [restriction, setRestriction] = React.useState<ZRestriction | null>(
    null,
  );
  const onInit = async () => {
    const options = await loadRefOptions(referenceId);
    setAllOptions(options);
    if (restrictValues) {
      // загрузка возможных ограничений
      const loaded = await loadRestrictions(attr.id);
      setRestriction(loaded);
      if (loaded) onRestrictionLoaded?.(attr.id, loaded);
    }
  };
  React.useEffect(() => {
    setWait(true);
    onInit()
      .finally(() => setWait(false))
      .catch(onError);
  }, [referenceId]);

  // Пока идет загрузка можно показать крутилку
  if (wait) return <Spin size="small" />;

  if (restriction) {
    return (
      <RefItemConditional
        restriction={restriction}
        allOptions={allOptions}
        isMulti={isMulti}
        value={value}
        onChange={onChange}
        viewType={viewType}
        extraProps={extraProps}
        selfClear={!onRestrictionLoaded}
      />
    );
  }

  // Нет ограничений по значению.
  return (
    <ReferenceItemBottom
      value={value}
      onChange={onChange}
      isMulti={isMulti}
      options={allOptions}
      viewType={viewType}
      extraProps={extraProps}
    />
  );
};

// ----------
interface PropsRefItemConditional {
  value: string[] | undefined;
  onChange: ((newValue?: unknown) => void) | undefined;
  restriction: ZRestriction;
  allOptions: ZOption[];
  isMulti: boolean;
  viewType: string | null | undefined;
  extraProps: AttrSelectProps;
  // Очищать ли собственное значение при смене ограничивающего атрибута.
  // false - каскадной очисткой владеет внешний стор (см. BuilderCtx.registerRestriction)
  selfClear: boolean;
}

const RefItemConditional: React.FC<PropsRefItemConditional> = (props) => {
  const {
    restriction,
    allOptions,
    isMulti,
    viewType,
    value,
    onChange,
    extraProps,
    selfClear,
  } = props;
  const restrictionValueRaw = Form.useWatch(
    String(restriction?.restrictionAttributeId ?? ""),
  );
  const options: ZOption[] = React.useMemo(() => {
    // Если нет зависимостей, то options = allOptions
    if (!restriction) return allOptions;
    // Множество доступных значений. Если таких нет, то в итоге будет пустой список.
    const allowed = getAllowedSourceIds(restriction, restrictionValueRaw);
    return allOptions.filter((opt) => allowed.has(opt.value));
  }, [allOptions, restriction, restrictionValueRaw]);

  // Если меняется список опций и в нём уже нет того значения, которое было выбрано ранее, тогда нужно очистить значение.
  // На карточке сущности каскадная очистка выполняется в EntityCardStore.resetDependentAttrs
  // строго по пользовательскому вводу (selfClear=false): эффект в компоненте реагирует
  // и на программные записи (переинициализация формы, пересчёт формул), из-за чего
  // значение стиралось в транзиентных состояниях. Локальный эффект оставлен
  // для форм без стора карточки (RegisterPage, MassEdit и т.п.). LPLM-5173
  React.useEffect(() => {
    if (!selfClear) return;
    // Путём эксперимента выяснено, что сначала всегда приходит restrictionValueRaw=undefined
    // Это некорректное значение, т.к. пустое значение соответствует []. Поэтому игнорируем undefined
    if (restrictionValueRaw && value && onChange) {
      const validValues = new Set<string>(
        options.map((option) => option.value),
      );
      const next = value.filter((singleValue) => validValues.has(singleValue));
      // Пишем в форму только при реальном изменении: холостой onChange помечает
      // поле touched и провоцирует посторонние каскады очистки. LPLM-5173
      if (next.length !== value.length) onChange(next);
    }
  }, [restrictionValueRaw]);

  return (
    <ReferenceItemBottom
      options={options}
      isMulti={isMulti}
      viewType={viewType}
      value={value}
      onChange={onChange}
      extraProps={extraProps}
    />
  );
};

// -----------

interface PropsReferenceItemBottom {
  value: string[] | undefined;
  onChange: ((newValue?: unknown) => void) | undefined;
  isMulti: boolean;
  viewType: string | null | undefined;
  options: ZOption[];
  extraProps: AttrSelectProps;
}

const ReferenceItemBottom: React.FC<PropsReferenceItemBottom> = (props) => {
  const { value, onChange, isMulti, options, viewType, extraProps } = props;
  if (!isMulti && (viewType === "RadioGroup" || viewType === "RadioButtons")) {
    const optionType = viewType === "RadioButtons" ? "button" : undefined;
    return (
      <Radio.Group
        value={value}
        onChange={onChange}
        optionType={optionType}
        options={options}
      />
    );
  }
  return (
    <Select
      value={value}
      onChange={(newValue) =>
        onChange?.(
          ifDef(newValue, (it) => (Array.isArray(it) ? it : [it])) ?? [],
        )
      }
      options={options}
      mode={isMulti ? "multiple" : undefined}
      allowClear
      {...extraProps}
    />
  );
};
